import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import {
    Badge,
    Button,
    Input,
    Select,
    SelectOption,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
} from '@freelancelabs/components';
import { EyeOutline, MagnifyingGlass } from '@freelancelabs/icons';
import { SORT_DIRECTION } from '@freelancelabs/shared';

import ListCard, { WRAPPING_HEADER } from '../../components/ListCard';
import { AccountUsersSort, MembershipDisplayStatus, SortDirection } from '../../graphql/__generated__/schema';
import { AccessibleAccountsDocument, AccountUsersDocument, RolesDocument } from '../../graphql/__generated__/types';
import FilterChips, { Chip } from './FilterChips';
import { SEVERAL, accountsCell, roleCell, somewhereNotice } from './aggregate';
import { EMPTY, formatDate } from './display';
import { STATUS_FILTER_ORDER, statusCategory, statusLabel } from './status';

const PAGE_SIZE = 25;

/** Long enough not to query on every keystroke, short enough not to feel laggy. */
const SEARCH_DEBOUNCE_MS = 300;

type StatusFilter = MembershipDisplayStatus | 'ALL';
type AccountFilter = string | 'ALL';

/**
 * Portal users: screen M12-04, list view (mockup §5.2).
 *
 * The list opens on **every account the actor may see** and the account is a filter,
 * like in the mockup. It used to be a path segment, which meant one could not reach
 * the screen, nor change account, nor read it at all without knowing an identifier:
 * an account in the URL is not a filter.
 *
 * The options of that filter come from `accessibleAccounts`, which is bounded by the
 * same function as the list itself, so the filter cannot offer an account the list
 * would refuse.
 *
 * **Sorting is the server's.** `accountUsers` takes the column and the direction, so
 * the order is that of the whole list; sorting the twenty-five rows on screen would
 * have looked like sorting the list without being one.
 *
 * **One row per person** (PCG-90). It used to be one row per membership, so somebody
 * attached to three accounts appeared three times and the footer said « N
 * utilisateurs » while counting attachments: 33 % too many on a realistic set. The
 * API now returns the person, carries the aggregate, and carries the detail alongside
 * it, so the row shows both: the account cell enumerates with a « +n », and the role
 * and status columns say « plusieurs » when the memberships disagree.
 *
 * Two things the data cannot say and this screen must: see `aggregate.ts`: the « +n »
 * counts **the accounts the reader may see**, and the role and status filters mean
 * **« somewhere »**.
 *
 * There is no membership identifier on a row any more, deliberately: the person is the
 * unit of work, as the sheet already established, and the row action opens the sheet.
 */
const UsersPage = () => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [status, setStatus] = useState<StatusFilter>('ALL');
    const [account, setAccount] = useState<AccountFilter>('ALL');
    const [sort, setSort] = useState<AccountUsersSort>(AccountUsersSort.LastName);
    const [direction, setDirection] = useState<SortDirection>(SortDirection.Asc);
    const [page, setPage] = useState(0);

    /**
     * The role filter lives in the URL and nowhere else. It is not a control of the
     * toolbar (the mockup has no role select) it is where « 4 utilisateurs » of the
     * roles list lands. Keeping it in the address makes that link shareable, and
     * removing its chip removes it from the address too.
     */
    const [searchParams, setSearchParams] = useSearchParams();
    const roleId = searchParams.get('role');

    const { data: accountsData } = useQuery(AccessibleAccountsDocument);
    const accounts = accountsData?.accessibleAccounts ?? [];

    // Only to name the role in its chip. Same query as the roles screens, so Apollo
    // answers from cache as soon as one of them has been opened.
    const { data: rolesData } = useQuery(RolesDocument, { skip: !roleId });
    const roleName = [...(rolesData?.portal ?? []), ...(rolesData?.backoffice ?? [])].find(
        (candidate) => candidate?.id === roleId
    )?.name;

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    // Any change of criteria sends us back to the first page: staying on page 3 of a
    // result set that now holds four rows shows an empty table and reads as a bug.
    useEffect(() => setPage(0), [debouncedSearch, status, account, roleId, sort, direction]);

    const { data, loading, error } = useQuery(AccountUsersDocument, {
        variables: {
            // Null and not omitted: the absence of an account is what asks for every
            // account, and it has to travel as such.
            organizationId: account === 'ALL' ? null : account,
            search: debouncedSearch || null,
            status: status === 'ALL' ? null : status,
            roleId,
            sort,
            direction,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
        },
    });

    const statusOptions: SelectOption<StatusFilter>[] = [
        { label: c('Users filter').t`All statuses`, value: 'ALL' },
        ...STATUS_FILTER_ORDER.map((value) => ({ label: statusLabel(value), value })),
    ];

    const accountOptions: SelectOption<AccountFilter>[] = [
        { label: c('Users filter').t`All accounts`, value: 'ALL' },
        ...accounts.filter(Boolean).map((option) => ({ label: option.name ?? '', value: option.id ?? '' })),
    ];

    /**
     * A sortable header. The DS carries the arrows and the `aria-sort`; the screen
     * only says which column is sorted, and clicking the sorted one flips it.
     */
    const sortable = (column: AccountUsersSort): Partial<TableHeaderCellProps> => ({
        direction:
            sort === column ? (direction === SortDirection.Asc ? SORT_DIRECTION.ASC : SORT_DIRECTION.DSC) : undefined,
        onSort: (next: SORT_DIRECTION) => {
            setSort(column);
            setDirection(next === SORT_DIRECTION.ASC ? SortDirection.Asc : SortDirection.Desc);
        },
    });

    /**
     * Widths add up to less than the 1180px of the page, so the nine columns of the
     * mockup fit without a horizontal scroll. They were wide enough to push « Plus de
     * détails » out of the card : an action nobody scrolls to find.
     */
    const columns: TableHeaderCellProps[] = (
        [
            {
                id: 'lastName',
                label: c('Users column').t`Last name`,
                minWidth: 95,
                ...sortable(AccountUsersSort.LastName),
            },
            { id: 'firstName', label: c('Users column').t`First name`, minWidth: 80 },
            { id: 'email', label: c('Users column').t`Email`, minWidth: 165 },
            { id: 'role', label: c('Users column').t`Role`, minWidth: 130 },
            {
                id: 'organization',
                label: c('Users column').t`Account`,
                minWidth: 180,
                // Attention, THE WARNING THE DATA CANNOT CARRY. The enumeration is bounded by
                // the actor's perimeter, which is correct and deliberate, but it
                // means two operators comparing their screens read two different
                // « +n » for the same person, and conclude the base disagrees with
                // itself. Said on the header, which is where somebody doubting the
                // figure looks, rather than repeated on every row.
                textHelp: c('Users column')
                    .t`Only the accounts you are allowed to see are listed and counted here. Someone with a wider portfolio may see more accounts for the same person — that is expected, not an inconsistency.`,
            },
            { id: 'status', label: c('Users column').t`Status`, minWidth: 105 },
            // 105 and not 95: a date in monospace measures 100px once the cells are
            // set in normal rather than medium, and the column cut it by one pixel.
            {
                id: 'invitedAt',
                label: c('Users column').t`Invited on`,
                minWidth: 105,
                ...sortable(AccountUsersSort.InvitedAt),
            },
            {
                id: 'activatedAt',
                label: c('Users column').t`Account created`,
                minWidth: 105,
                ...sortable(AccountUsersSort.ActivatedAt),
            },
            // Wide enough for the button plus the cell's own padding: at 125 the last
            // pixels of « Plus de détails » were cut by the edge of the card.
            { id: 'actions', label: '', minWidth: 150, textAlign: 'end' },
            // « Date d'invitation » and « Création du compte » do not fit on one line at
            // these widths, and the design system cuts them with an ellipsis. The mockup
            // lets its headers run onto two lines, so we do the same rather than widen
            // two columns for the sake of their titles.
        ] as TableHeaderCellProps[]
    ).map((column) => ({ ...column, className: WRAPPING_HEADER }));

    /**
     * The design system's cells hold one line and cut with an ellipsis. The mockup
     * wraps instead, and an account named « GMD : Direction des Systèmes
     * d'Information » is unreadable cut at a third of its length.
     *
     * `!` and not a plain utility: `@freelancelabs/styles` is imported **after**
     * Tailwind in `index.scss`, so at equal specificity the design system wins and
     * the class would do nothing at all.
     */
    const wrapping = '!whitespace-normal !break-words py-2';

    const result = data?.accountUsers;
    const items = result?.items ?? [];
    const total = result?.total ?? 0;
    const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
    // ttag refuses an expression inside a localized template: the two numbers are
    // computed here so the string holds nothing but placeholders.
    const currentPageNumber = page + 1;
    const pageCount = lastPage + 1;

    const filtered = Boolean(debouncedSearch) || status !== 'ALL' || account !== 'ALL' || Boolean(roleId);

    const clearRole = () => setSearchParams({}, { replace: true });

    const clearFilters = () => {
        setSearch('');
        setDebouncedSearch('');
        setStatus('ALL');
        setAccount('ALL');
        clearRole();
    };

    /** Active filters, each removable on its own: reading them off the controls is
     *  not enough: a collapsed select shows its value, a search field scrolled out of
     *  view shows nothing. */
    // Said only while one of the two « somewhere » filters is on: see `aggregate.ts`.
    const somewhere = somewhereNotice(Boolean(roleId), status !== 'ALL');

    const chips: Chip[] = [
        ...(debouncedSearch
            ? [
                  {
                      id: 'search',
                      label: c('Users filter').t`Search`,
                      value: debouncedSearch,
                      onRemove: () => {
                          setSearch('');
                          setDebouncedSearch('');
                      },
                  },
              ]
            : []),
        ...(status !== 'ALL'
            ? [
                  {
                      id: 'status',
                      label: c('Users filter').t`Status`,
                      value: statusLabel(status),
                      onRemove: () => setStatus('ALL'),
                  },
              ]
            : []),
        ...(roleId
            ? [
                  {
                      id: 'role',
                      label: c('Users filter').t`Role`,
                      // The name once the referential is there, the identifier meanwhile:
                      // a chip with a hole in it is worse than a chip with a UUID.
                      value: roleName ?? roleId,
                      onRemove: clearRole,
                  },
              ]
            : []),
        ...(account !== 'ALL'
            ? [
                  {
                      id: 'account',
                      label: c('Users filter').t`Account`,
                      value: accountOptions.find((option) => option.value === account)?.label ?? account,
                      onRemove: () => setAccount('ALL'),
                  },
              ]
            : []),
    ];

    return (
        <section className="flex flex-col gap-5">
            <header className="flex flex-wrap items-start gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-text-900">{c('Users').t`Users`}</h1>
                </div>
                {/* The onboarding journey: the person, their accounts, what they carry on
                    each, then the dashboards and the partner list. Its last two steps read
                    the catalogues this screen and « Listes de partenaires » now fill: the
                    referring account manager is still outside it. */}
                <Link to="/utilisateurs/nouveau" className="ml-auto">
                    <Button category="default">{c('Users').t`Onboard a user`}</Button>
                </Link>
            </header>

            {/* PCG-141: the three configuration panels of an account used to hang here,
                shown as soon as the account filter narrowed to one. They live on the
                account sheet now, and they are not duplicated: two paths to one gesture
                are two places to correct, and the day one of them gains a refusal the
                other keeps offering the button. The account filter below narrows a list
                of users and nothing else. */}

            {error ? (
                <p className="text-danger-600" role="alert">
                    {c('Users').t`This list could not be loaded.`}
                </p>
            ) : (
                <ListCard
                    label={c('Users').t`Users of the account`}
                    toolbar={
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap items-center gap-3">
                                <Input
                                    value={search}
                                    onValue={setSearch}
                                    clearable
                                    prefix={<MagnifyingGlass size={16} />}
                                    placeholder={c('Users filter').t`Name, email or job title`}
                                    aria-label={c('Users filter').t`Search a user`}
                                    className="max-w-sm"
                                />
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find((option) => option.value === status)}
                                    onChange={(option) => setStatus(option.value)}
                                    className="max-w-xs"
                                />
                                {/* Only when there is a choice to make: a single accessible
                                    account makes the filter a control with one option, which
                                    reads as broken. */}
                                {accounts.length > 1 && (
                                    <Select
                                        options={accountOptions}
                                        value={accountOptions.find((option) => option.value === account)}
                                        onChange={(option) => setAccount(option.value)}
                                        className="max-w-xs"
                                    />
                                )}
                            </div>
                            <FilterChips chips={chips} onClearAll={clearFilters} />
                            {/* Only while a role or status filter is on. « Porte ce
                                rôle » means « sur AU MOINS UN compte », and an operator
                                looking at a list they believe is scoped to one account
                                reads it as « sur ce compte ». Permanent, this sentence
                                would be noise; absent, the misreading is uncatchable. */}
                            {somewhere && <p className="text-xs text-text-600">{somewhere}</p>}
                        </div>
                    }
                    footer={
                        !loading &&
                        items.length > 0 && (
                            <>
                                {/* « correspondant » is inside the plural, not appended to
                                    it: glued on as its own string it did not agree with
                                    the count, and the footer read « 1 utilisateur
                                    correspondants ». French adjectives agree; a fragment
                                    concatenated outside `ngettext` cannot. */}
                                <span>
                                    {filtered
                                        ? c('Users list').ngettext(
                                              msgid`${total} matching user`,
                                              `${total} matching users`,
                                              total
                                          )
                                        : c('Users list').ngettext(msgid`${total} user`, `${total} users`, total)}
                                </span>
                                {total > PAGE_SIZE && (
                                    <nav
                                        className="ml-auto flex items-center gap-3"
                                        aria-label={c('Users').t`Pagination`}
                                    >
                                        <Button
                                            category="weak"
                                            size="small"
                                            disabled={page === 0}
                                            onClick={() => setPage((current) => current - 1)}
                                        >
                                            {c('Action').t`Previous`}
                                        </Button>
                                        <span className="font-mono text-xs">
                                            {c('Pagination').t`Page ${currentPageNumber} of ${pageCount}`}
                                        </span>
                                        <Button
                                            category="weak"
                                            size="small"
                                            disabled={page >= lastPage}
                                            onClick={() => setPage((current) => current + 1)}
                                        >
                                            {c('Action').t`Next`}
                                        </Button>
                                    </nav>
                                )}
                            </>
                        )
                    }
                >
                    <>
                        <Table>
                            <TableHeader resizable cells={columns} />
                            <TableBody loading={loading} loadingRows={5} loadingColumns={columns.length}>
                                {items.map((user) => {
                                    const accounts = accountsCell(user);
                                    const role = roleCell(user);
                                    return (
                                        // The person is the row, so the person is the key.
                                        <TableRow key={user.userId}>
                                            <TableCell className={wrapping}>
                                                {/* The weight is on the text, not on the cell: the
                                                card sets every cell to normal through a rule of
                                                two classes, which a single class on the cell
                                                cannot outweigh even with `!`. */}
                                                <span className="font-semibold">{user.lastName ?? EMPTY}</span>
                                            </TableCell>
                                            <TableCell className={wrapping}>{user.firstName ?? EMPTY}</TableCell>
                                            <TableCell className={`${wrapping} font-mono text-xs`}>
                                                {user.email}
                                            </TableCell>
                                            <TableCell className={wrapping}>
                                                {/* A badge, as in the mockup: the role is a label one
                                                scans down the column, not a sentence to read.
                                                « Plusieurs » is toned down: it is the absence of
                                                a single answer, not a role of that name. */}
                                                <Badge category={role.aggregated ? 'default' : 'info'} size="small">
                                                    {role.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className={wrapping}>
                                                {accounts.first}
                                                {accounts.surplus > 0 && (
                                                    // The names ride along as the tooltip: a bare
                                                    // « +2 » sends the reader to the sheet for
                                                    // something the row already knows.
                                                    <span
                                                        className="ml-1 cursor-help rounded border border-border-norm bg-background-weak px-1 text-xs text-text-600"
                                                        title={accounts.othersTitle}
                                                        tabIndex={0}
                                                    >
                                                        +{accounts.surplus}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {user.status ? (
                                                    <Badge category={statusCategory(user.status)} size="small">
                                                        {statusLabel(user.status)}
                                                    </Badge>
                                                ) : (
                                                    <Badge category="default" size="small">
                                                        {SEVERAL()}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">
                                                {formatDate(user.invitedAt)}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">
                                                {/* An empty cell would read as « unknown ». Nobody
                                                activated is a state, and it is the one that calls
                                                for a resend. */}
                                                {user.activatedAt ? (
                                                    formatDate(user.activatedAt)
                                                ) : (
                                                    <span className="font-sans text-text-500">
                                                        {c('Users').t`Not activated`}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-end">
                                                <Button
                                                    to={`/utilisateurs/${user.userId}`}
                                                    category="weak"
                                                    size="small"
                                                    icon
                                                    aria-label={c('Users').t`View the user`}
                                                    title={c('Users').t`View the user`}
                                                >
                                                    <EyeOutline size={16} />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {!loading && items.length === 0 && (
                            <div className="fl-table-empty flex flex-col items-center gap-3" aria-live="polite">
                                {filtered ? (
                                    <>
                                        <p>{c('Users').t`No user matches these criteria.`}</p>
                                        <p className="text-xs text-text-600">
                                            {c('Users').t`Widen the criteria: remove a filter or shorten the search.`}
                                        </p>
                                        <Button category="weak" size="small" onClick={clearFilters}>
                                            {c('Users').t`Clear filters`}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <p>{c('Users').t`No user on the accounts you can see.`}</p>
                                        <p className="text-xs text-text-600">
                                            {c('Users')
                                                .t`Onboarding the first user will give them access to the portal.`}
                                        </p>
                                    </>
                                )}
                            </div>
                        )}
                    </>
                </ListCard>
            )}
        </section>
    );
};

export default UsersPage;
